/**
 * AuthStore - Central authentication state management
 *
 * Replaces: AuthContext, AuthProvider, useAuth
 *
 * Ported from: contexts/AuthProvider.tsx
 *
 * Responsibilities:
 * - Firebase authentication state (onAuthStateChanged)
 * - Backend authorization via /auth/me
 * - Token refresh handling
 * - Session storage management (pending registration)
 * - Race condition prevention
 * - Permission and role checks
 *
 * Benefits over Context:
 * - Computed properties automatically memoized
 * - Actions traceable in MobX DevTools
 * - No Context re-render propagation
 * - Cross-store access via this.root
 */

import { makeAutoObservable, runInAction } from "mobx";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  User,
} from "firebase/auth";
import { auth } from "@/shared/lib/firebase";
import type { RootStore } from "@/stores/RootStore";
import type { AuthStatus, AuthMeResponse } from "../types/auth.types";
import { StorageKeys } from "@/shared/constants";

const PENDING_REGISTRATION_KEY = StorageKeys.pendingRegistration;

/**
 * Session storage helpers - isolated for safety
 * Uses sessionStorage instead of localStorage so that pending registration state
 * is automatically cleared when the browser tab/window is closed.
 */
const _safeSetPendingRegistration = (v: boolean) => {
  try {
    if (v) {
      window.sessionStorage.setItem(PENDING_REGISTRATION_KEY, "1");
    } else {
      window.sessionStorage.removeItem(PENDING_REGISTRATION_KEY);
    }
  } catch {
    // ignore
  }
};

const _safeHasPendingRegistration = (): boolean => {
  try {
    return window.sessionStorage.getItem(PENDING_REGISTRATION_KEY) === "1";
  } catch {
    return false;
  }
};

export class AuthStore {
  // =============================================
  // Observable State
  // =============================================
  user: User | null = null;
  authMe: AuthMeResponse | null = null;
  status: AuthStatus = "unknown";
  loading = true;

  // =============================================
  // Private State (Non-Observable)
  // =============================================
  private _unsubscribe: (() => void) | null = null;
  private _googleProvider = new GoogleAuthProvider();
  private _verifyingRef = false;
  private _verifiedUidRef: string | null = null;
  private _authStatusRef: AuthStatus = "unknown";
  private _authorizingUidRef: string | null = null;

  constructor(private root: RootStore) {
    makeAutoObservable(this);
    this._initAuthListener();
  }

  // =============================================
  // Computed Properties
  // =============================================

  /**
   * User is authenticated AND authorized
   */
  get isAuthenticated(): boolean {
    return this.user !== null && this.status === "allowed";
  }

  /**
   * User has admin role
   */
  get isAdmin(): boolean {
    return this.authMe?.global_roles.includes("admin") ?? false;
  }

  /**
   * User has developer role (or is admin)
   */
  get isDeveloper(): boolean {
    return (
      this.isAdmin || (this.authMe?.global_roles.includes("developer") ?? false)
    );
  }

  /**
   * User belongs to at least one organization (partner)
   */
  get isPartner(): boolean {
    return (this.authMe?.orgs.length ?? 0) > 0;
  }

  /**
   * User's permission list
   */
  get permissions(): string[] {
    return this.authMe?.permissions ?? [];
  }

  /**
   * Display name for UI (fallback chain)
   */
  get displayName(): string {
    return this.user?.displayName || this.user?.email?.split("@")[0] || "User";
  }

  // =============================================
  // Authentication Actions (Arrow functions to preserve 'this' binding)
  // =============================================

  /**
   * Sign in with email and password
   */
  signInWithEmail = async (email: string, password: string): Promise<void> => {
    this._verifyingRef = true;
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await this._authorizeAndSetState(cred.user);
    } finally {
      this._verifyingRef = false;
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  /**
   * Sign in with Google popup
   */
  signInWithGoogle = async (): Promise<void> => {
    this._verifyingRef = true;
    try {
      const result = await signInWithPopup(auth, this._googleProvider);
      await this._authorizeAndSetState(result.user);
    } catch (error) {
      await firebaseSignOut(auth);
      throw error;
    } finally {
      this._verifyingRef = false;
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  /**
   * Sign up with email and password
   * Does NOT call /auth/me - user must complete registration first
   */
  signUpWithEmail = async (email: string, password: string): Promise<void> => {
    this._verifyingRef = true;
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      _safeSetPendingRegistration(true);
    } finally {
      this._verifyingRef = false;
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  /**
   * Sign out and reset all state
   */
  signOut = async (): Promise<void> => {
    _safeSetPendingRegistration(false);
    this._verifiedUidRef = null;
    this._authStatusRef = "unknown";
    this._authorizingUidRef = null;

    await firebaseSignOut(auth);

    runInAction(() => {
      this.user = null;
      this.authMe = null;
      this.status = "unknown";
    });
  };

  /**
   * Send password reset email
   */
  resetPassword = async (email: string): Promise<void> => {
    await sendPasswordResetEmail(auth, email);
  };

  /**
   * Get current user's Firebase ID token
   */
  getIdToken = async (): Promise<string | null> => {
    if (!this.user) return null;
    return this.user.getIdToken();
  };

  // =============================================
  // Authorization Checks
  // =============================================

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission: string): boolean {
    return this.permissions.includes(permission);
  }

  /**
   * Check if user has a specific role
   */
  hasRole(role: string): boolean {
    return this.authMe?.global_roles.includes(role) ?? false;
  }

  // =============================================
  // Private Methods
  // =============================================

  /**
   * Initialize Firebase auth listener
   * Handles automatic state synchronization
   */
  private _initAuthListener(): void {
    this._unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      await this._handleAuthStateChange(currentUser);
    });
  }

  /**
   * Handle Firebase auth state changes
   * Includes all defensive logic from original AuthProvider
   */
  private async _handleAuthStateChange(
    currentUser: User | null,
  ): Promise<void> {
    // No user - clear state
    if (!currentUser) {
      runInAction(() => {
        this.user = null;
        this.authMe = null;
        this.status = "unknown";
        this.loading = false;
      });
      return;
    }

    // If we have a persisted "pending registration" marker,
    // start from a clean slate on reload
    if (_safeHasPendingRegistration()) {
      try {
        await firebaseSignOut(auth);
        _safeSetPendingRegistration(false);
      } catch {
        // keep marker
      }

      runInAction(() => {
        this.user = null;
        this.authMe = null;
        this.status = "unknown";
        this.loading = false;
      });
      return;
    }

    // If sign-in flow is already doing authorization, do nothing here
    if (this._verifyingRef) return;

    // Skip if we've already verified this user (prevents duplicate calls)
    if (
      this._verifiedUidRef === currentUser.uid &&
      this._authStatusRef === "allowed"
    ) {
      runInAction(() => {
        this.loading = false;
      });
      return;
    }

    // Skip if we're already authorizing this user (prevents race conditions)
    if (this._authorizingUidRef === currentUser.uid) {
      return;
    }

    // Mark that we're authorizing this user
    this._authorizingUidRef = currentUser.uid;

    try {
      await this._authorizeAndSetState(currentUser);
    } catch {
      await firebaseSignOut(auth);
      runInAction(() => {
        this.user = null;
        this.authMe = null;
        this.status = "unknown";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /**
   * Authorize user with backend and update state
   * Core authorization logic with token refresh handling
   */
  private async _authorizeAndSetState(u: User): Promise<void> {
    const token = await u.getIdToken();

    try {
      // Call /auth/me via RootStore API
      const me = await this.root.api.auth.authMe(token);

      // Handle token refresh if needed
      if (me.needs_token_refresh) {
        const refreshed = await u.getIdToken(true);
        const me2 = await this.root.api.auth.authMe(refreshed);

        if (me2.needs_token_refresh) {
          // Defensive: avoid infinite refresh loops
          throw new Error("Token refresh loop");
        }

        if (!me2.allowed) {
          runInAction(() => {
            this.status = "forbidden";
            this.authMe = me2;
            this.user = null;
          });
          return;
        }

        // Success after refresh
        this._verifiedUidRef = u.uid;
        this._authStatusRef = "allowed";

        runInAction(() => {
          this.status = "allowed";
          this.authMe = me2;
          this.user = u;
        });
        return;
      }

      // Not allowed
      if (!me.allowed) {
        _safeSetPendingRegistration(false);
        runInAction(() => {
          this.status = "forbidden";
          this.authMe = me;
          this.user = null;
        });
        return;
      }

      // Success - user is allowed
      _safeSetPendingRegistration(false);
      this._verifiedUidRef = u.uid;
      this._authStatusRef = "allowed";

      runInAction(() => {
        this.status = "allowed";
        this.authMe = me;
        this.user = u;
      });
    } catch (e) {
      const code =
        typeof e === "object" && e && "code" in e
          ? String((e as { code: unknown }).code)
          : "";

      if (code === "NOT_VERIFIED") {
        runInAction(() => {
          this.status = "not_verified";
          this.authMe = null;
          this.user = null;
        });
        return;
      }

      if (code === "NEEDS_REGISTRATION") {
        // Mark so that a browser refresh resets the state
        _safeSetPendingRegistration(true);
        runInAction(() => {
          this.status = "needs_registration";
          this.authMe = null;
          this.user = null;
        });
        return;
      }

      if (code === "FORBIDDEN") {
        runInAction(() => {
          this.status = "forbidden";
          this.authMe = null;
          this.user = null;
        });
        return;
      }

      // Unknown error - rethrow
      throw e;
    }
  }

  /**
   * Cleanup method - call when disposing store
   */
  dispose(): void {
    this._unsubscribe?.();
  }
}
